import React, { useState } from 'react'
import { setCookie } from '../app/axios/CookieConfig'
import { useNavigate } from 'react-router-dom'
import * as api from '../app/axios/api'
import { useAuth } from '../context/AuthContext'
import { toast } from 'sonner'
import { useRole } from '../context/RoleContext'

const Login = () => {
  const { setIsLogin } = useAuth()

  const { setRole, setProfileEmail, setProfileName, setProfileId } = useRole()

  const navigate = useNavigate()
  const [state, setState] = useState({
    email: '',
    password: '',
  })
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const validateForm = () => {
    const errors = {}
    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/
    if (!state.email) {
      errors.email = 'Email is required'
    } else if (!emailPattern.test(state.email)) {
      errors.email = 'Invalid email format'
    }
    if (!state.password) {
      errors.password = 'Password is required'
    } else if (state.password.length < 4) {
      errors.password = 'Password must be at least 6 characters long'
    }
    return errors
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setIsLoading(true)

    const validationErrors = validateForm()
    setErrors(validationErrors)
    if (Object.keys(validationErrors).length > 0) {
      setIsLoading(false)
      return
    }

    try {
      const res = await api.login({
        email: state.email,
        password: state.password,
      })

      const TokenExpiresAt = Date.parse(res.data.token_expires_at)
      const currentMilies = Date.now()
      const token = res.data.token
      const userRole = res.data.role
      const expiryInSeconds = parseInt((TokenExpiresAt - currentMilies) / 1000)

      setCookie(
        'token',
        token,
        parseInt((TokenExpiresAt - currentMilies) / 1000)
      )

      setIsLogin(true)
      setCookie('role', userRole, expiryInSeconds)
      setCookie('email', res.data.email, expiryInSeconds)
      setCookie('name', res.data.name, expiryInSeconds)
      setCookie('id', res.data.id)

      setRole(res.data.role)
      setProfileEmail(res.data.email)
      setProfileName(res.data.name)
      setProfileId(res.data.id)
      toast.success('Login Success')
      navigate('/')
    } catch (error) {
      console.log(error)
      if (error?.message) {
        toast.error(error?.message)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setState((prevState) => ({ ...prevState, [name]: value }))
    setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }))
  }

  const handleClick = () => {
    console.log('Forgot password feature is not implemented yet.')
  }

  return (
    <div className='min-h-screen relative overflow-hidden'>
      {/* Animated Background */}
      <div className='absolute inset-0 bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900'>
        <div className='absolute top-0 -left-4 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob'></div>
        <div className='absolute top-0 -right-4 w-72 h-72 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000'></div>
        <div className='absolute -bottom-8 left-20 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000'></div>
      </div>

      <div className='relative min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-4xl w-full'>
          <div className='bg-white/10 backdrop-blur-lg border border-white/20 rounded-3xl shadow-2xl overflow-hidden'>
            <div className='flex flex-col lg:flex-row'>
              {/* Left Side - Illustration */}
              <div className='lg:w-1/2 bg-gradient-to-br from-purple-600 to-blue-600 p-8 lg:p-12 flex items-center justify-center relative overflow-hidden'>
                <div className='absolute inset-0 bg-black/10'></div>
                <div className='relative text-center text-white z-10'>
                  <div className='mb-8'>
                    <div className='w-32 h-32 mx-auto bg-white/20 rounded-full flex items-center justify-center mb-6 backdrop-blur-sm border border-white/30'>
                      <svg
                        className='w-16 h-16 text-white'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={1.5}
                          d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'
                        />
                      </svg>
                    </div>
                  </div>
                  <h1 className='text-4xl font-bold mb-4'>Welcome Back!</h1>
                  <p className='text-xl opacity-90 mb-8'>
                    Sign in to access your dashboard and continue your journey
                    with us.
                  </p>
                  <div className='flex items-center justify-center space-x-2 text-sm opacity-75'>
                    <div className='w-2 h-2 bg-white rounded-full animate-pulse'></div>
                    <span>Secure Login</span>
                  </div>
                </div>
              </div>

              {/* Right Side - Form */}
              <div className='lg:w-1/2 p-8 lg:p-12'>
                <div className='max-w-md mx-auto'>
                  {/* Header */}
                  <div className='text-center mb-8'>
                    <h2 className='text-3xl font-bold text-white mb-2'>
                      Inflection
                    </h2>
                    <p className='text-white/70'>
                      Enter your credentials to continue
                    </p>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit} className='space-y-6'>
                    {/* Email Field */}
                    <div className='space-y-2'>
                      <label className='block text-white/90 text-sm font-medium'>
                        Email Address
                      </label>
                      <div className='relative'>
                        <input
                          className={`w-full px-4 py-4 bg-white/10 border ${
                            errors.email ? 'border-red-400' : 'border-white/20'
                          } rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder-white/50 backdrop-blur-sm transition-all duration-300`}
                          type='email'
                          value={state.email}
                          name='email'
                          placeholder='Enter your email address'
                          onChange={handleChange}
                        />
                        <div className='absolute inset-y-0 right-0 pr-3 flex items-center'>
                          <svg
                            className='w-5 h-5 text-white/40'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207'
                            />
                          </svg>
                        </div>
                      </div>
                      {errors.email && (
                        <p className='text-red-400 text-sm flex items-center'>
                          <svg
                            className='w-4 h-4 mr-1'
                            fill='currentColor'
                            viewBox='0 0 20 20'
                          >
                            <path
                              fillRule='evenodd'
                              d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z'
                              clipRule='evenodd'
                            />
                          </svg>
                          {errors.email}
                        </p>
                      )}
                    </div>

                    {/* Password Field */}
                    <div className='space-y-2'>
                      <div className='flex justify-between items-center'>
                        <label className='block text-white/90 text-sm font-medium'>
                          Password
                        </label>

                        <button
                          onClick={handleClick}
                          className='text-purple-400 hover:text-purple-300 text-sm font-medium transition-colors duration-200'
                        >
                          Forgot password?
                        </button>
                      </div>
                      <div className='relative'>
                        <input
                          className={`w-full px-4 py-4 bg-white/10 border ${
                            errors.password
                              ? 'border-red-400'
                              : 'border-white/20'
                          } rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder-white/50 backdrop-blur-sm transition-all duration-300`}
                          type='password'
                          name='password'
                          placeholder='Enter your password'
                          value={state.password}
                          onChange={handleChange}
                        />
                        <div className='absolute inset-y-0 right-0 pr-3 flex items-center'>
                          <svg
                            className='w-5 h-5 text-white/40'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'
                            />
                          </svg>
                        </div>
                      </div>
                      {errors.password && (
                        <p className='text-red-400 text-sm flex items-center'>
                          <svg
                            className='w-4 h-4 mr-1'
                            fill='currentColor'
                            viewBox='0 0 20 20'
                          >
                            <path
                              fillRule='evenodd'
                              d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z'
                              clipRule='evenodd'
                            />
                          </svg>
                          {errors.password}
                        </p>
                      )}
                    </div>

                    {/* Submit Button */}
                    <button
                      type='submit'
                      disabled={isLoading}
                      className='w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-transparent shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none'
                    >
                      {isLoading ? (
                        <div className='flex items-center justify-center'>
                          <svg
                            className='animate-spin -ml-1 mr-3 h-5 w-5 text-white'
                            fill='none'
                            viewBox='0 0 24 24'
                          >
                            <circle
                              className='opacity-25'
                              cx='12'
                              cy='12'
                              r='10'
                              stroke='currentColor'
                              strokeWidth='4'
                            ></circle>
                            <path
                              className='opacity-75'
                              fill='currentColor'
                              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                            ></path>
                          </svg>
                          Signing In...
                        </div>
                      ) : (
                        <div className='flex items-center justify-center'>
                          <span>Sign In</span>
                          <svg
                            className='ml-2 w-4 h-4'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M13 7l5 5m0 0l-5 5m5-5H6'
                            />
                          </svg>
                        </div>
                      )}
                    </button>
                  </form>

                  {/* Footer */}
                  <div className='text-center mt-8'>
                    <p className='text-white/60 text-sm'>
                      Don't have an account?{' '}
                      <a
                        href='#'
                        className='text-purple-400 hover:text-purple-300 font-semibold transition-colors duration-200'
                      >
                        Create one now
                      </a>
                    </p>
                  </div>

                  {/* Social Login */}
                  <div className='mt-8'>
                    <div className='relative'>
                      <div className='absolute inset-0 flex items-center'>
                        <div className='w-full border-t border-white/20'></div>
                      </div>
                      <div className='relative flex justify-center text-sm'>
                        <span className='px-4 bg-transparent text-white/60'>
                          Or continue with
                        </span>
                      </div>
                    </div>
                    <div className='mt-6 grid grid-cols-2 gap-3'>
                      <button className='w-full inline-flex justify-center py-3 px-4 border border-white/20 rounded-lg bg-white/5 text-white hover:bg-white/10 transition-all duration-200 backdrop-blur-sm'>
                        <svg className='w-5 h-5' viewBox='0 0 24 24'>
                          <path
                            fill='currentColor'
                            d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
                          />
                          <path
                            fill='currentColor'
                            d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                          />
                          <path
                            fill='currentColor'
                            d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
                          />
                          <path
                            fill='currentColor'
                            d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                          />
                        </svg>
                        <span className='ml-2'>Google</span>
                      </button>
                      <button className='w-full inline-flex justify-center py-3 px-4 border border-white/20 rounded-lg bg-white/5 text-white hover:bg-white/10 transition-all duration-200 backdrop-blur-sm'>
                        <svg
                          className='w-5 h-5'
                          fill='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path d='M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' />
                        </svg>
                        <span className='ml-2'>Facebook</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  )
}

export default Login
